import { defineStore } from 'pinia'
import { ref } from 'vue'
import { meApi } from '@/api/me'
import type { MenuNode } from '@/types/api'

/**
 * 动态菜单（权限模型 v2）：唯一数据源是 GET /api/me/v1/me/menus?systemUuid=。
 * 服务端已按「公司 → 系统 → 服务 → 权限点」过滤，前端不做二次裁剪，也不内置兜底菜单。
 *
 * 传入的 systemUuid 是**控制台自身所属系统**（appStore.consoleSystemUuid，见 stores/app.ts）：
 * 菜单是导航结构，属于控制台，切换"管理对象"时不得跟着变。
 */
export const useMenuStore = defineStore('menu', () => {
  const tree = ref<MenuNode[]>([])
  /** 当前菜单所属系统：换系统必须重拉取，否则看到的是上一个系统的导航结构。 */
  const loadedSystemUuid = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  async function load(systemUuid: string, force = false): Promise<void> {
    if (!force && loadedSystemUuid.value === systemUuid && tree.value.length > 0) {
      return
    }
    loading.value = true
    error.value = null
    try {
      tree.value = await meApi.menus({ systemUuid, scope: 'management' })
      loadedSystemUuid.value = systemUuid
    } catch (e) {
      tree.value = [] // 失败不保留上一系统的菜单：宁可空导航，也不要把 A 的菜单当成 B 的
      loadedSystemUuid.value = null
      error.value = e instanceof Error ? e.message : '菜单加载失败'
    } finally {
      loading.value = false
    }
  }

  /** 登出清理：与权限同批重置，避免下一次登录先闪出上一个账号的导航结构。 */
  function reset(): void {
    tree.value = []
    loadedSystemUuid.value = null
    error.value = null
  }

  return { tree, loadedSystemUuid, loading, error, load, reset }
})
